/**
 * State hooks for the onboarding flow and the enrolled profile.
 *
 * useOnboarding drives the screen-by-screen state machine. It tracks
 * the current step, the user's typed name, and the four captured
 * answers. When all four answers are in, it commits the profile to
 * storage and fires onComplete so the parent view can flip.
 *
 * useProfile is a tiny subscriber to localStorage so the Me tab can
 * know whether to render onboarding or the conversation scaffold.
 */
import { useCallback, useEffect, useState } from "react";

import {
  checkSpeechPermission,
  requestSpeechPermission,
  transcribeAudio,
} from "@/hooks/useSpeechTranscription";
import { computeVoiceprint } from "@/hooks/useVoiceprint";

import { QUESTIONS, INTRO_SCREENS } from "./content";
import { loadProfile, saveProfile } from "./profileStorage";
import type { EnrollmentAnswer, OnboardingStep, UserProfile } from "./types";

/**
 * Read the stored profile and re-check on focus so switching tabs
 * after completing onboarding picks up the new state immediately.
 */
export function useProfile(): UserProfile | null {
  const [profile, setProfile] = useState<UserProfile | null>(() => loadProfile());

  useEffect(() => {
    const refresh = () => setProfile(loadProfile());
    window.addEventListener("storage", refresh);
    window.addEventListener("focus", refresh);
    return () => {
      window.removeEventListener("storage", refresh);
      window.removeEventListener("focus", refresh);
    };
  }, []);

  return profile;
}

interface UseOnboardingResult {
  step: OnboardingStep;
  name: string;
  setName: (value: string) => void;
  answers: EnrollmentAnswer[];
  advance: () => void;
  submitAnswer: (answer: EnrollmentAnswer) => void;
  processingError: string | null;
}

interface VoiceprintSummaryLite {
  id: string;
  dim: number;
  sampleCount: number;
  enrolledAt: string;
}

interface UseOnboardingOptions {
  onComplete: (profile: UserProfile) => void;
}

export function useOnboarding({ onComplete }: UseOnboardingOptions): UseOnboardingResult {
  const [step, setStep] = useState<OnboardingStep>({ kind: "intro", index: 0 });
  const [name, setName] = useState("");
  const [answers, setAnswers] = useState<EnrollmentAnswer[]>([]);
  const [voiceprint, setVoiceprint] = useState<VoiceprintSummaryLite | null>(null);
  const [processingError, setProcessingError] = useState<string | null>(null);

  /**
   * Move to the next step in the flow. Intro screens advance in
   * sequence; after the last one we hit the name screen, then the
   * brief, then each question, then the complete screen. Called on
   * Next button taps for non-voice screens.
   */
  const advance = useCallback(() => {
    setStep((current) => {
      switch (current.kind) {
        case "intro": {
          const next = current.index + 1;
          if (next < INTRO_SCREENS.length) {
            return { kind: "intro", index: next };
          }
          return { kind: "name" };
        }
        case "name":
          return { kind: "brief" };
        case "brief":
          return { kind: "question", index: 0 };
        case "question": {
          const next = current.index + 1;
          if (next < QUESTIONS.length) {
            return { kind: "question", index: next };
          }
          return { kind: "processing" };
        }
        case "processing":
        case "complete":
          return current;
      }
    });
  }, []);

  /**
   * Commit the answer for the currently displayed question and
   * advance to the next one. The full answer object (question text,
   * audio path, duration) is appended to answers so the Phase 2a-2
   * transcriber and Phase 2b voiceprint computer can find them all.
   */
  const submitAnswer = useCallback(
    (answer: EnrollmentAnswer) => {
      setAnswers((prev) => [...prev, answer]);
      advance();
    },
    [advance]
  );

  /**
   * Processing step — after the four answers are captured, transcribe
   * each audio file on-device via SFSpeechRecognizer and attach the
   * text to its EnrollmentAnswer. On any failure (no permission, file
   * missing, recognizer unavailable) the answer keeps its audio but
   * loses its transcript — the flow always advances.
   */
  useEffect(() => {
    if (step.kind !== "processing") return;
    let cancelled = false;

    const run = async () => {
      setProcessingError(null);

      // Speech permission + transcription. Everything in this block is
      // best-effort — if it fails the flow continues without transcripts.
      let finalAnswers = answers;
      try {
        let status = await checkSpeechPermission();
        if (status === "notDetermined") {
          status = await requestSpeechPermission();
        }

        if (status === "granted") {
          const transcribed: EnrollmentAnswer[] = [];
          for (const a of answers) {
            try {
              const t = await transcribeAudio(a.audioPath);
              transcribed.push({ ...a, transcript: t.text });
            } catch (err) {
              console.warn("Transcription failed for one answer:", err);
              transcribed.push(a);
            }
          }
          finalAnswers = transcribed;
        }
      } catch (err) {
        console.warn("Speech permission / transcription block failed:", err);
        if (!cancelled) setProcessingError(String(err));
      }

      // Voiceprint. Also best-effort. Failures do not block completion.
      try {
        const summary = await computeVoiceprint(
          finalAnswers.map((a) => a.audioPath),
          "user_self"
        );
        if (!cancelled) {
          setVoiceprint({
            id: summary.id,
            dim: summary.dim,
            sampleCount: summary.sampleCount,
            enrolledAt: summary.enrolledAt,
          });
        }
      } catch (err) {
        console.warn("Voiceprint compute failed:", err);
        if (!cancelled && !processingError) {
          setProcessingError(`Voiceprint: ${String(err)}`);
        }
      }

      if (cancelled) return;
      setAnswers(finalAnswers);
      setStep({ kind: "complete" });
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [step, answers, processingError]);

  /**
   * Once the flow reaches the complete step, persist the profile and
   * fire onComplete. The short delay lets the "I heard you" line sit
   * on screen before the app flips to the enrolled Me view.
   */
  useEffect(() => {
    if (step.kind !== "complete") return;
    const profile: UserProfile = {
      name: name.trim() || "You",
      enrolledAt: new Date().toISOString(),
      answers,
      ...(voiceprint ? { voiceprint } : {}),
    };
    saveProfile(profile);
    const t = setTimeout(() => onComplete(profile), 2600);
    return () => clearTimeout(t);
  }, [step, name, answers, voiceprint, onComplete]);

  return { step, name, setName, answers, advance, submitAnswer, processingError };
}
